import { Component, effect, inject, input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  DataObject,
  ReportInfo,
  ReportRecord,
  ReportView,
  TagOutput,
  TemplateProjectionMap,
} from '../../models/report.model';
import { PROPERTY_MAP_ADDRESS } from '../summary-view/summary-view.model';
import { MiqMoment } from '@miq/utils';
import { ReportExportViewService } from '../../report-export-view.service';
import { ReportAllRecordsImage } from '../../models/template.model';
import { ReportImagesService } from '../../report-images.service';
import { DynamicLocaleService } from '../../dynamic-locale.service';
import { IconLoaderService } from '../../icon-loader.service';
import { ActionsToolbarComponent } from '../presentation-view/actions-toolbar/actions-toolbar.component';
import { NgTemplateOutlet } from '@angular/common';
import { DevelopmentPipelineViewImageComponent } from '../development-pipeline-view/rep-development-pipeline-view-image/development-pipeline-view-image.component';
import { L10nTranslationModule } from 'angular-l10n';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { SelectTemplateOutletPipe } from '../../shared/pipes/select-template-outlet.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-tag-output-view',
  templateUrl: './tag-output-view.component.html',
  styleUrl: './tag-output-view.component.scss',
  imports: [
    ActionsToolbarComponent,
    DevelopmentPipelineViewImageComponent,
    NgTemplateOutlet,
    L10nTranslationModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    SelectTemplateOutletPipe
],
})
export class TagOutputViewComponent implements OnInit, OnDestroy {
  readonly reportData = input.required<ReportInfo>();
  readonly currentView = input.required<ReportView>();
  readonly reportLocale = input.required<number>();
  readonly columns = input<{ [entity: string]: { [columnField: string]: any } }>();
  readonly templateProjectionMap = input<TemplateProjectionMap>();
  readonly entity = input<string>();

  displayedColumns: string[] = ['FLOOR', 'SQM', 'SQFT'];
  financialTermColumns: string[] = ['occupancyCost', 'perSqft'];
  financialTermsData = [];
  statusAndCond: DataObject;
  property: TagOutput;
  totalProperties: any[] = [];
  showNavigationArrows = false;
  record: ReportRecord[];
  summaryAddressPaths: DataObject;

  private readonly reportExportViewService = inject(ReportExportViewService);
  private readonly reportImagesService = inject(ReportImagesService);
  private readonly localeService = inject(DynamicLocaleService);
  private readonly iconLoaderService = inject(IconLoaderService);
  private lastFetchedImagesReportId: number | string | null = null;

  constructor() {
    effect(() => {
      const reportData = this.reportData();
      const reportLocale = this.reportLocale();
      this.totalProperties = reportData.tagOutput;
      this.localeService.setReportLocaleId(reportLocale);
      this.getAddressMaps();
      if (reportData?.reportId != null && reportData.reportId !== this.lastFetchedImagesReportId) {
        this.lastFetchedImagesReportId = reportData.reportId;
        this.fetchAdditionalImagesOfReport();
      }
      if (this.totalProperties.length > 1) {
        this.onRecordChange(0);
      }
    });
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  ngOnInit(): void {
    // required by angular-l10n
  }

  navRecordNum(event) {
    this.onRecordChange(event - 1);
  }

  onRecordChange(index) {
    this.property = this.totalProperties[index];
    if (this.entity() === 'availability') {
      this.record = this.reportData().records.filter(rec => rec.associatedProperty.entity.id === this.property.id);
    } else {
      this.record = this.reportData().records.filter(rec => rec.data.entity.id === this.property.id);
    }
    this.setStatusAndCond();
    this.setFinancialTermData();
  }

  private setStatusAndCond() {
    if (this.property.statusAndCond) {
      const { statusAndCond } = this.property;

      let readyToOccupy: string = null;
      if (statusAndCond.readyToOccupy != null) {
        const ukDateFormat = 'DD/MM/YYYY';
        readyToOccupy = MiqMoment.format(this.property.statusAndCond.readyToOccupy, ukDateFormat);
      }

      this.statusAndCond = { ...statusAndCond, readyToOccupy };
    }
  }

  private setFinancialTermData() {
    this.financialTermsData = [
      { key: 'template.guideRent', value: this.property.financialTerms.guideRent },
      { key: 'template.businessRate', value: this.property.financialTerms.businessRate },
      { key: 'template.serviceCharge', value: this.property.financialTerms.serviceCharge },
    ];
  }

  private getAddressMaps() {
    this.summaryAddressPaths = {
      postalAddress: this.reportData().entity
        ? PROPERTY_MAP_ADDRESS[this.reportData().entity]
        : PROPERTY_MAP_ADDRESS.property,
    };
  }

  /**
   * this method is used to fetch the additional image id of the report.
   */
  private fetchAdditionalImagesOfReport() {
    this.reportExportViewService.getImages(this.reportData().reportId).subscribe((res: ReportAllRecordsImage) => {
      this.reportImagesService.setReportRecordImages(res);
    });
  }
}
